/**
 * Ingredient lines as recipes write them ("8 tablespoons of salted butter", "1 1/2 cups all
 * purpose flour (6.75 ounces)", "2 large eggs, beaten", "salt to taste"): the amount, its unit,
 * an explicit weight (which always wins), and the food's name to search for. Then grams for a
 * food picked for the line, from that food's own sizes when one fits.
 *
 * Shared, so an import's best guesses (the server) and picking a food for a line (the recipe
 * editor) read a line the same way. A line is cut to IMPORT_LIMITS.ingredientLine characters
 * first, which bounds every pattern's work.
 */
import type { Serving } from './foods';
import { IMPORT_LIMITS } from './recipeImport';
import { MAX_ENTRY_GRAMS } from './recipes';
import { tokenize } from './search';

const UNICODE_FRACTIONS: Record<string, number> = {
  '½': 1 / 2,
  '⅓': 1 / 3,
  '⅔': 2 / 3,
  '¼': 1 / 4,
  '¾': 3 / 4,
  '⅕': 1 / 5,
  '⅖': 2 / 5,
  '⅗': 3 / 5,
  '⅘': 4 / 5,
  '⅙': 1 / 6,
  '⅚': 5 / 6,
  '⅛': 1 / 8,
  '⅜': 3 / 8,
  '⅝': 5 / 8,
  '⅞': 7 / 8,
};

const decimal = (n: number) => String(Math.round(n * 1000) / 1000);

/** "1 1/2" and "1-1/2" → "1.5", "1½" → "1.5", "3/4" → "0.75", "1/2-3/4" → "0.5-0.75". */
export function normalizeFractions(text: string): string {
  return (
    text
      .replace(/(?:(\d+)\s*)?([½⅓⅔¼¾⅕⅖⅗⅘⅙⅚⅛⅜⅝⅞])/g, (_, whole: string | undefined, frac: string) => decimal(Number(whole ?? 0) + UNICODE_FRACTIONS[frac]!))
      // A mixed number's whole part doesn't follow a fraction: "1/2-3/4" is a range, not "1/(2 3/4)".
      .replace(/(?<![\d/⁄.])(\d+)(?:\s+|-)(\d+)\s*[/⁄]\s*(\d+)/g, (match: string, whole: string, num: string, den: string) =>
        Number(den) > 0 ? decimal(Number(whole) + Number(num) / Number(den)) : match,
      )
      .replace(/(\d+)\s*[/⁄]\s*(\d+)/g, (match: string, num: string, den: string) => (Number(den) > 0 ? decimal(Number(num) / Number(den)) : match))
  );
}

/** Amounts are kept to 0.1 g. */
const tenth = (n: number) => Math.round(n * 10) / 10;

// ——— Units

/** Millilitres in each volume unit: US cups and spoons, as nutrition labels count them. A pinch, a dash and a drop are tiny volumes. */
export const VOLUME_ML = { tsp: 5, tbsp: 15, cup: 240, 'fl oz': 30, pint: 473, quart: 946, gallon: 3785, ml: 1, cl: 10, dl: 100, l: 1000, pinch: 0.3, dash: 0.6, drop: 0.05 } as const;
/** Grams in each weight unit. */
export const WEIGHT_G = { g: 1, kg: 1000, oz: 28.35, lb: 453.6 } as const;
/** How big each one is, for things counted ("2 large eggs"). */
const SIZES = ['small', 'medium', 'large', 'extra large', 'jumbo'] as const;
/** Things counted in a unit of their own ("3 cloves garlic", "1 can black beans"). */
const COUNT_UNITS = [
  'clove',
  'slice',
  'piece',
  'stick',
  'can',
  'jar',
  'package',
  'bag',
  'box',
  'bottle',
  'carton',
  'container',
  'bunch',
  'head',
  'sprig',
  'stalk',
  'leaf',
  'handful',
  'scoop',
  'sheet',
  'strip',
  'cube',
  'fillet',
  'knob',
  'pat',
  'serving',
  'wedge',
  'loaf',
  'ear',
] as const;

export type VolumeUnit = keyof typeof VOLUME_ML;
type MassUnit = keyof typeof WEIGHT_G;
type SizeUnit = (typeof SIZES)[number];
type CountUnit = (typeof COUNT_UNITS)[number];
/** A unit as the parser names it: "tbsp", "cup", "g", "large", "clove", … */
export type Unit = VolumeUnit | MassUnit | SizeUnit | CountUnit;

export const isVolume = (unit: Unit | null): unit is VolumeUnit => unit !== null && Object.hasOwn(VOLUME_ML, unit);
export const isMass = (unit: Unit | null): unit is MassUnit => unit !== null && Object.hasOwn(WEIGHT_G, unit);
export const isSize = (unit: Unit | null): unit is SizeUnit => unit !== null && (SIZES as readonly string[]).includes(unit);

/** Each unit as recipes write it, tried in order at the start of the text after the amount. */
const UNIT_WORDS: ReadonlyArray<readonly [RegExp, Unit]> = [
  [/^fl(?:uid)?\.?\s*(?:oz\b|ounces?\b)\.?/i, 'fl oz'],
  [/^(?:tablespoons?|tbsps?|tbls?|tbs|tblsp)\b\.?/i, 'tbsp'],
  // A capital T is a tablespoon and a small t a teaspoon.
  [/^T(?=[\s.]|$)\.?/, 'tbsp'],
  [/^(?:teaspoons?|tsps?)\b\.?/i, 'tsp'],
  [/^t(?=[\s.]|$)\.?/, 'tsp'],
  [/^(?:cups?\b|c(?=[\s.]|$))\.?/i, 'cup'],
  [/^(?:pints?|pts?)\b\.?/i, 'pint'],
  [/^(?:quarts?|qts?)\b\.?/i, 'quart'],
  [/^(?:gallons?|gal)\b\.?/i, 'gallon'],
  [/^(?:millilit(?:er|re)s?|mls?)\b\.?/i, 'ml'],
  [/^(?:centilit(?:er|re)s?|cl)\b\.?/i, 'cl'],
  [/^(?:decilit(?:er|re)s?|dl)\b\.?/i, 'dl'],
  [/^(?:lit(?:er|re)s?|ltrs?|l)\b\.?/i, 'l'],
  [/^(?:kilograms?|kilos?|kgs?)\b\.?/i, 'kg'],
  [/^(?:grams?|grammes?|gr|g)\b\.?/i, 'g'],
  [/^(?:ounces?|oz)\b\.?/i, 'oz'],
  [/^(?:pounds?|lbs?)\b\.?/i, 'lb'],
  [/^pinch(?:es)?\b/i, 'pinch'],
  [/^dash(?:es)?\b/i, 'dash'],
  [/^drops?\b/i, 'drop'],
  [/^cloves?\b/i, 'clove'],
  [/^slices?\b/i, 'slice'],
  [/^(?:pieces?|pcs?)\b\.?/i, 'piece'],
  [/^sticks?\b/i, 'stick'],
  [/^(?:cans?|tins?)\b/i, 'can'],
  [/^jars?\b/i, 'jar'],
  [/^(?:packages?|pkgs?|packets?|packs?|sachets?|envelopes?)\b\.?/i, 'package'],
  [/^bags?\b/i, 'bag'],
  [/^box(?:es)?\b/i, 'box'],
  [/^bottles?\b/i, 'bottle'],
  [/^cartons?\b/i, 'carton'],
  [/^(?:containers?|tubs?)\b/i, 'container'],
  [/^bunch(?:es)?\b/i, 'bunch'],
  [/^heads?\b/i, 'head'],
  [/^sprigs?\b/i, 'sprig'],
  [/^stalks?\b/i, 'stalk'],
  [/^(?:leaf|leaves)\b/i, 'leaf'],
  [/^handfuls?\b/i, 'handful'],
  [/^scoops?\b/i, 'scoop'],
  [/^sheets?\b/i, 'sheet'],
  [/^strips?\b/i, 'strip'],
  [/^cubes?\b/i, 'cube'],
  [/^fill?ets?\b/i, 'fillet'],
  [/^knobs?\b/i, 'knob'],
  [/^pats?\b/i, 'pat'],
  [/^servings?\b/i, 'serving'],
  [/^wedges?\b/i, 'wedge'],
  [/^(?:loaf|loaves)\b/i, 'loaf'],
  [/^ears?\b/i, 'ear'],
];

/** Units a line can start with and still mean one: "Pinch of nutmeg", "Handful of spinach". */
const ONE_WITHOUT_COUNT: ReadonlySet<Unit> = new Set(['pinch', 'dash', 'drop', 'handful', 'knob', 'sprig', 'bunch', 'clove']);

const SIZE = /^(extra[-\s]?large|x-?large|xl|small|medium|med|large|lg|big|jumbo)(?:[-\s]sized)?\b\.?/i;

function sizeOf(word: string): SizeUnit {
  const w = word.toLowerCase().replace(/[-\s]/g, '');
  if (w === 'extralarge' || w === 'xlarge' || w === 'xl') return 'extra large';
  if (w === 'med') return 'medium';
  if (w === 'lg' || w === 'big') return 'large';
  return w as SizeUnit;
}

function readUnit(text: string): { unit: Unit; length: number } | null {
  for (const [pattern, unit] of UNIT_WORDS) {
    const m = pattern.exec(text);
    if (m) return { unit, length: m[0].length };
  }
  return null;
}

// ——— Explicit weights and volumes

/** An optional "2 x", an amount or a range ("1-2", "1 to 2"), then a weight or metric volume unit ("14-ounce" too). */
const MEASURE =
  /(?:(\d+(?:\.\d+)?)\s*[x×]\s*)?(\d+(?:\.\d+)?)(?:\s*(?:-|–|—|to)\s*(\d+(?:\.\d+)?))?\s*-?\s*(kilograms?|kilos?|kgs?|grams?|grammes?|gr|g|ounces?|oz|pounds?|lbs?|millilit(?:er|re)s?|mls?|lit(?:er|re)s?|l)\b/i;

/** The first weight (in grams) or metric volume (in ml) in some text. A range counts as its middle. */
function measureIn(text: string): { amount: number; kind: 'g' | 'ml' } | null {
  const m = MEASURE.exec(text);
  if (!m) return null;
  const times = m[1] ? Number(m[1]) : 1;
  const amount = m[3] ? (Number(m[2]) + Number(m[3])) / 2 : Number(m[2]);
  const unit = m[4]!.toLowerCase();
  let factor = 1;
  let kind: 'g' | 'ml' = 'g';
  if (unit.startsWith('k')) factor = 1000;
  else if (unit === 'oz' || unit.startsWith('ounce')) factor = 28.35;
  else if (unit.startsWith('lb') || unit.startsWith('pound')) factor = 453.6;
  else if (unit.startsWith('m')) kind = 'ml';
  else if (unit.startsWith('l')) {
    factor = 1000;
    kind = 'ml';
  }
  const value = times * amount * factor;
  return value > 0 ? { amount: value, kind } : null;
}

/** Within what one ingredient can be, kept to 0.1, else null. */
const withinLimit = (n: number) => (n > 0 && n <= IMPORT_LIMITS.ingredientGrams ? tenth(n) : null);

// ——— Reading a line

/** One line, cut short, with fractions and decimal commas as plain decimals and no bullet in front. */
function prepare(raw: string): string {
  const line = raw
    .slice(0, IMPORT_LIMITS.ingredientLine)
    // Control and invisible characters.
    .replace(/[\u0000-\u001f\u007f​-‏‪-‮⁦-⁩﻿]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return (
    normalizeFractions(line)
      // "1,5" is a decimal comma; "1,000" a thousands separator.
      .replace(/(\d),(\d{3})(?!\d)/g, '$1$2')
      .replace(/(\d),(\d{1,2})(?!\d)/g, '$1.$2')
      // Bullets and checkboxes ("▢ 1 cup", "- 2 eggs"), but not ".5".
      .replace(/^(?:[^\p{L}\p{N}(.]|\.(?!\d))+/u, '')
      // A note in front ("(optional) 2 tbsp honey"), but not a weight ("(15 oz) can").
      .replace(/^\((?![^()]*\d)[^()]*\)\s*/, '')
  );
}

const WORD_NUMBERS: Record<string, number> = {
  a: 1,
  an: 1,
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  eleven: 11,
  twelve: 12,
  half: 0.5,
  dozen: 12,
};
const NUMBER = /^(\d+(?:\.\d+)?)(?:\s*(?:-|–|—|to|or)\s*(\d+(?:\.\d+)?))?/i;
// Followed by a space, so "Half-and-half" isn't half of "-and-half".
const WORD_NUMBER = /^(an?|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|half|dozen)(?=\s)/i;
const AFTER_A: Record<string, number> = { couple: 2, few: 3, dozen: 12, half: 0.5 };
/** Words about the measure itself, between the count and the unit: "1 heaping tablespoon", "1 packed cup". */
const MEASURE_WORDS = /^(?:(?:firmly|lightly|loosely)\s+)?(?:heaping|heaped|level(?:l?ed)?|scant|generous|rounded|good|full|packed)\s+/i;
const WEIGHT_UNIT_WORD =
  '(?:kilograms?|kilos?|kgs?|grams?|grammes?|gr|g|ounces?|oz|pounds?|lbs?|millilit(?:er|re)s?|mls?|lit(?:er|re)s?|l)\\b\\.?';
const TIMES = new RegExp(`^[x×]\\s*\\d+(?:\\.\\d+)?\\s*${WEIGHT_UNIT_WORD}\\s*`, 'i');
/** Each item's weight, written between the count and the unit: "1 14-ounce can", "2 8-oz packages". */
const EACH_WEIGHT = new RegExp(`^\\d+(?:\\.\\d+)?\\s*-?\\s*${WEIGHT_UNIT_WORD}\\s*`, 'i');
const CONTAINER = /^(?:cans?|tins?|jars?|packages?|packets?|packs?|bags?|box(?:es)?|bottles?|cartons?|containers?|tubs?)\b\s*/i;
/** Containers come in sizes: "2 cans (15 oz) beans" is two 15 oz cans. A stick of butter doesn't: "2 sticks (8 oz)" is 8 oz. */
const SIZED_CONTAINERS: ReadonlySet<Unit> = new Set(['can', 'jar', 'package', 'bag', 'box', 'bottle', 'carton', 'container']);
/** A second amount after the first: an alternative ("100g/3½oz") or more of it ("1 tbsp + 1 tsp", "1 lb 2 oz"). */
const SECOND_AMOUNT = /^(\/|\+|plus\b|and\b)?\s*(\d+(?:\.\d+)?)\s*-?\s*/i;
/** Count units that can come after the food's name: "2 garlic cloves", "3 celery stalks". */
const TRAILING_UNITS: ReadonlySet<Unit> = new Set(['clove', 'slice', 'stalk', 'sprig', 'wedge', 'head']);

interface Amount {
  quantity: number | null;
  unit: Unit | null;
  /** The text after the amount: the food's name, with any notes. */
  rest: string;
  /** Each item's weight: in brackets right after the count ("2 (15 oz) cans") or a sized container, written before the unit ("1 14-ounce can"), or marked "each". */
  perItem: string | null;
  /** Grams added by a second weight: "1 lb 2 oz" is 2 oz more than its first weight. */
  extraGrams: number;
}

/** The amount a line (or a food's size, "1 tbsp (14 g)") starts with. */
function readAmount(text: string): Amount {
  let rest = text.replace(/^(?:about|approx(?:imately|\.)?|around|roughly|~)\s*/i, '');
  let quantity: number | null = null;
  const num = NUMBER.exec(rest);
  if (num) {
    quantity = num[2] !== undefined ? (Number(num[1]) + Number(num[2])) / 2 : Number(num[1]);
    rest = rest.slice(num[0].length);
  } else {
    const word = WORD_NUMBER.exec(rest);
    if (word) {
      const w = word[1]!.toLowerCase();
      quantity = WORD_NUMBERS[w]!;
      rest = rest.slice(word[0].length);
      if (w === 'a' || w === 'an') {
        const next = /^\s+(couple|few|dozen|half)(?=\s)/i.exec(rest);
        if (next) {
          quantity = AFTER_A[next[1]!.toLowerCase()]!;
          rest = rest.slice(next[0].length);
        }
      }
      if (quantity === 0.5) {
        // "half a dozen", "half a lemon".
        const dozen = /^\s+(?:an?\s+)?dozen(?=\s)/i.exec(rest);
        const article = /^\s+an?(?=\s)/i.exec(rest);
        if (dozen) {
          quantity = 6;
          rest = rest.slice(dozen[0].length);
        } else if (article) {
          rest = rest.slice(article[0].length);
        }
      }
    }
  }
  if (quantity !== null) {
    // "1 and a half", "1 and 1/2" (which reads "1 and 0.5" by now), "2 dozen".
    const half = /^\s+and\s+(?:a\s+half\b|(0?\.\d+)(?!\d))/i.exec(rest);
    if (half) {
      quantity += half[1] ? Number(half[1]) : 0.5;
      rest = rest.slice(half[0].length);
    }
    const dozen = /^\s*dozens?\b/i.exec(rest);
    if (dozen) {
      quantity *= 12;
      rest = rest.slice(dozen[0].length);
    }
  }
  rest = rest.trimStart();

  let perItem: string | null = null;
  let unit: Unit | null = null;
  let extraGrams = 0;
  if (quantity !== null) {
    const bracket = /^\(([^()]*)\)\s*/.exec(rest);
    if (bracket) {
      perItem = bracket[1]!;
      rest = rest.slice(bracket[0].length);
    }
    // "2 x 400 g cans": that weight is in the line's measure already.
    const times = TIMES.exec(rest);
    if (times) rest = rest.slice(times[0].length);
    // "1 14-ounce can": each one's weight.
    const each = times ? null : EACH_WEIGHT.exec(rest);
    if (each && perItem === null) {
      perItem = each[0];
      rest = rest.slice(each[0].length);
    }
    const modifier = MEASURE_WORDS.exec(rest);
    if (modifier) rest = rest.slice(modifier[0].length);
    const size = SIZE.exec(rest);
    if (size) {
      unit = sizeOf(size[1]!);
      rest = rest.slice(size[0].length).trimStart();
    }
    const found = readUnit(rest);
    if (found) {
      unit = found.unit;
      rest = rest.slice(found.length).trimStart();
      // A second amount: "100g/3½oz" says it again; "1 tbsp + 1 tsp" and "1 lb 2 oz" add to it.
      for (let i = 0; i < 3; i++) {
        const second = SECOND_AMOUNT.exec(rest);
        const secondUnit = second ? readUnit(rest.slice(second[0].length)) : null;
        if (!second || !secondUnit) break;
        const more = Number(second[2]);
        if (second[1] !== '/') {
          if (isVolume(unit) && isVolume(secondUnit.unit)) quantity = Math.round((quantity + (more * VOLUME_ML[secondUnit.unit]) / VOLUME_ML[unit]) * 1000) / 1000;
          else if (isMass(unit) && isMass(secondUnit.unit)) extraGrams += more * WEIGHT_G[secondUnit.unit];
        }
        rest = rest.slice(second[0].length + secondUnit.length).trimStart();
      }
    }
    // "400 g can chickpeas": the container isn't part of the name.
    if (isVolume(unit) || isMass(unit)) {
      const container = CONTAINER.exec(rest);
      if (container) rest = rest.slice(container[0].length);
    }
    // "2 cans (15 oz) black beans": a sized container's weight is each one's, unless it says it's the total.
    const after = /^\(([^()]*)\)/.exec(rest);
    if (after && perItem === null && unit !== null && SIZED_CONTAINERS.has(unit) && !/\b(?:total|in all|combined|altogether)\b/i.test(after[1]!)) {
      perItem = after[1]!;
    }
    // "2 chicken breasts (about 8 ounces each)", wherever the bracket is.
    const marked = /\(([^()]*\beach\b[^()]*)\)/i.exec(rest);
    if (marked && perItem === null) perItem = marked[1]!;
  } else {
    const found = readUnit(rest);
    if (found && ONE_WITHOUT_COUNT.has(found.unit)) {
      quantity = 1;
      unit = found.unit;
      rest = rest.slice(found.length).trimStart();
    }
  }
  rest = rest.replace(/^of\s+/i, '');
  return { quantity, unit, rest, perItem, extraGrams };
}

// ——— The food's name

/** Words about preparing or choosing the food, not the food itself: left out of the search. */
const PREP_WORDS = new Set([
  'chopped',
  'diced',
  'minced',
  'sliced',
  'cubed',
  'halved',
  'quartered',
  'julienned',
  'crumbled',
  'mashed',
  'pureed',
  'peeled',
  'deveined',
  'seeded',
  'deseeded',
  'cored',
  'pitted',
  'trimmed',
  'rinsed',
  'drained',
  'thawed',
  'defrosted',
  'melted',
  'softened',
  'beaten',
  'whisked',
  'sifted',
  'packed',
  'divided',
  'finely',
  'coarsely',
  'roughly',
  'thinly',
  'thickly',
  'lightly',
  'firmly',
  'loosely',
  'freshly',
  'fresh',
  'ripe',
  'very',
  'organic',
  'boneless',
  'skinless',
  'heaping',
  'heaped',
  'level',
  'leveled',
  'levelled',
  'scant',
  'generous',
  'rounded',
  'about',
  'approximately',
  'approx',
  'good-quality',
  'homemade',
  'store-bought',
]);

/** A note after the name: "to taste", "for garnish", "plus more for dusting", "divided". */
const REMARK =
  /\s+(?:to taste|to serve|for (?:the )?(?:garnish(?:ing)?|serving|dusting|frying|greasing|drizzling|sprinkling|topping|brushing|decorating|pan)|as (?:needed|required|desired)|if (?:desired|needed|you like)|optional|plus (?:more|extra)|or (?:more|less|to taste|as needed)|divided|at room temperature|room temperature)\b.*$/i;

/** Lines whose amount is up to the cook. */
const TO_TASTE = /\b(?:to taste|as needed|as required|for (?:garnish(?:ing)?|serving|dusting|frying|greasing|drizzling|sprinkling|topping|brushing)|optional|if desired|to serve)\b/i;

/**
 * Words that name a kind of something, so in "kosher or sea salt" the first choice shares the
 * last word ("kosher salt"). Anything else stands alone: "butter or olive oil" is butter.
 */
const KIND_WORDS = new Set([
  'kosher',
  'sea',
  'table',
  'frozen',
  'dried',
  'canned',
  'red',
  'white',
  'yellow',
  'green',
  'black',
  'brown',
  'light',
  'dark',
  'golden',
  'chicken',
  'vegetable',
  'beef',
  'turkey',
  'pork',
  'whole',
  'unsalted',
  'salted',
  'sweet',
  'hot',
  'mild',
  'smoked',
  'raw',
  'cooked',
  'plain',
  'regular',
  'lemon',
  'lime',
  'orange',
  'apple',
  'olive',
  'coconut',
  'almond',
  'soy',
  'oat',
  'rice',
  'skim',
  'nonfat',
  'lowfat',
  'low-fat',
  'heavy',
]);

/** "kosher or sea salt" → "kosher salt", "butter or olive oil" → "butter", "or frozen peas" → "frozen peas". */
function firstChoice(text: string): string {
  const s = text.replace(/^(?:or|and\/or)\s+/i, '');
  const m = /^(.*?)\s+(?:or|and\/or)\s+(.+)$/i.exec(s);
  if (!m) return s;
  const left = m[1]!.trim().split(/\s+/).filter(Boolean);
  const right = m[2]!.trim().split(/\s+/);
  if (left.length === 0) return right.join(' ');
  if (left.length === 1 && right.length >= 2 && KIND_WORDS.has(left[0]!.toLowerCase())) return [left[0], ...right.slice(1)].join(' ');
  return left.join(' ');
}

/** The food's name for search: no notes, brackets, remarks or preparation words. */
function cleanName(text: string): string {
  let s = text
    // Brackets hold notes and weights ("(6.75 ounces)", "(I like raw cane sugar)"); an unclosed one runs to the end.
    .replace(/\([^()]*\)?/g, ' ')
    .replace(/\[[^[\]]*\]?/g, ' ');
  // "eggs, beaten": what follows a comma is about the food, not the food.
  s = s.split(/[,;]/)[0]!.replace(REMARK, '');
  // "butter/margarine" → "butter".
  s = s.replace(/(\p{L})\s*\/\s*\p{L}.*$/u, '$1');
  s = s
    .split(/\s+/)
    .filter((w) => w !== '' && !PREP_WORDS.has(w.toLowerCase().replace(/[.!]+$/, '')))
    .join(' ')
    .replace(/^(?:of|the|some)\s+/i, '');
  s = firstChoice(s)
    .replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}%)]+$/gu, '')
    .replace(/\s+/g, ' ')
    .trim();
  return s.slice(0, 80).trim();
}

// ——— The line as a whole

/** What a line says, read for search and amounts. */
export interface IngredientLine {
  /** How many: "1 1/2" → 1.5, "2-3" → 2.5 (the middle of a range), "a" → 1. Null when there's none ("salt to taste"). */
  quantity: number | null;
  /** The unit, normalized: "tbsp", "cup", "g", "clove", or a size ("large"). Null for a plain count ("1 egg") or no amount. */
  unit: Unit | null;
  /** Grams from an explicit weight ("6.75 ounces", "200 g", "1 (15 oz) can"). When there's one, it's the amount. */
  grams: number | null;
  /** Millilitres from an explicit metric volume ("250 ml", "1 cup (240 ml)"), when there's no weight. */
  ml: number | null;
  /** The food to search for: "8 tablespoons of salted butter" → "salted butter". Empty for a heading or a bare amount. */
  name: string;
  /** No amount, and it's up to the cook: "salt, to taste", "parsley, for garnish". */
  toTaste: boolean;
}

const NOTHING: IngredientLine = { quantity: null, unit: null, grams: null, ml: null, name: '', toTaste: false };

export function parseIngredientLine(raw: string): IngredientLine {
  const text = prepare(raw);
  // A section heading ("For the frosting:") isn't an ingredient.
  if (text === '' || text.endsWith(':')) return NOTHING;
  const amount = readAmount(text);
  const each = amount.perItem !== null && amount.quantity !== null ? measureIn(amount.perItem) : null;
  const measure = each ? { amount: each.amount * amount.quantity!, kind: each.kind } : measureIn(text);
  let unit = amount.unit;
  let name = cleanName(amount.rest);
  // "2 garlic cloves": the unit after the name.
  if (amount.quantity !== null && unit === null) {
    const last = /\s(\S+)$/.exec(name);
    const trailing = last ? readUnit(last[1]!) : null;
    if (trailing && trailing.length === last![1]!.length && TRAILING_UNITS.has(trailing.unit)) {
      unit = trailing.unit;
      name = name.slice(0, last!.index).trim();
    }
  }
  return {
    quantity: amount.quantity,
    unit,
    grams: measure?.kind === 'g' ? withinLimit(measure.amount + (each ? 0 : amount.extraGrams)) : null,
    ml: measure?.kind === 'ml' ? withinLimit(measure.amount) : null,
    name,
    toTaste: amount.quantity === null && TO_TASTE.test(text),
  };
}

/** What to search for a line's food: "1/2 cup white sugar (I like …)" → "white sugar". */
export function searchTextFor(line: string): string {
  return parseIngredientLine(line).name;
}

/**
 * Grams from an explicit weight or volume in an ingredient line: "200 g", "1 (14.5 oz) can",
 * "1-1/2 lb", "2 x 400 g", "1,5 kg", "250 ml" (about 1 g per ml). A range counts as its middle.
 * Null when there's none, or it's more than one ingredient can be.
 */
export function gramsFromLine(line: string): number | null {
  const { grams, ml } = parseIngredientLine(line);
  const whole = Math.round(grams ?? ml ?? 0);
  return whole > 0 && whole <= IMPORT_LIMITS.ingredientGrams ? whole : null;
}

// ——— Grams for a food picked for a line

export interface LineAmount {
  grams: number;
  /** Where the grams came from: the line's own weight, one of the food's sizes, its density, or a guess. */
  basis: 'weight' | 'size' | 'volume' | 'guess';
  /** A rough amount (about 1 g per ml, or one of the food's sizes for want of a better one): the person should check it. */
  check: boolean;
}

/** What a food picked for a line needs: its sizes, and whether it's measured in ml. */
export interface FoodSizes {
  name?: string;
  servings: readonly Serving[];
  liquid?: boolean;
}

/** One of a food's sizes, read: "1 cup diced (140 g)" is 1 cup of 140 g, with the word "diced". */
export interface FoodSize {
  grams: number;
  quantity: number;
  unit: Unit | null;
  /** The size's other words ("chopped" in "1 cup, chopped"), as search compares them. */
  words: string[];
  /** Those words as the label writes them, without brackets: "chopped", "liquid egg whites". */
  text: string;
}

/** Words that don't describe anything. */
const FILLER = new Set(['of', 'and', 'or', 'with', 'the', 'a', 'an', 'in', 'for']);

/** Words as search compares them (lowercase, singular: "tomatoes" is "tomato"), without numbers or filler. */
const wordsOf = (text: string) => tokenize(text).filter((w) => /\p{L}/u.test(w) && !FILLER.has(w));

/** A food's sizes, read from their labels. Sizes without a weight are left out. */
export function sizesOf(servings: readonly Serving[]): FoodSize[] {
  return servings.flatMap((s) => {
    if (!(s.grams > 0)) return [];
    const amount = readAmount(prepare(s.label));
    const rest = amount.rest.replace(/\([^()]*\)?/g, ' ');
    const words = wordsOf(rest);
    const text = rest.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '').replace(/\s+/g, ' ');
    return [{ grams: s.grams, quantity: amount.quantity && amount.quantity > 0 ? amount.quantity : 1, unit: amount.unit, words, text }];
  });
}

/** Of the sizes in a unit, the one whose words the line has ("1 cup chopped"), else a plain one, else the first. */
export function bestSize(sizes: readonly FoodSize[], words: ReadonlySet<string>): FoodSize | undefined {
  return sizes.find((s) => s.words.length > 0 && s.words.every((w) => words.has(w))) ?? sizes.find((s) => s.words.length === 0) ?? sizes[0];
}

/** The largest of a food's volume sizes ("1 cup (227 g)" over "2 tbsp (30 g)"): it gives the most precise density. */
export function largestVolume(sizes: readonly FoodSize[]): (FoodSize & { unit: VolumeUnit }) | undefined {
  return sizes
    .filter((s): s is FoodSize & { unit: VolumeUnit } => isVolume(s.unit))
    .sort((a, b) => b.quantity * VOLUME_ML[b.unit] - a.quantity * VOLUME_ML[a.unit])[0];
}

/**
 * Grams of `food` for a line, in this order: the line's own weight ("6.75 ounces"); the food's
 * size in the line's unit ("1 tbsp (14 g)" makes 8 tbsp 112 g; "1 large (50 g)" makes 1 egg
 * 50 g); a volume through the food's density, from another of its volume sizes (or 1 g per ml
 * for a drink); and only then a rough amount to check: about 1 g per ml (1 tbsp = 15 ml,
 * 1 tsp = 5 ml, 1 cup = 240 ml), a pinch for "to taste", or the food's first size.
 */
export function amountForFood(text: string, food: FoodSizes): LineAmount {
  const done = (grams: number, basis: LineAmount['basis'], check: boolean): LineAmount => {
    const capped = Math.min(MAX_ENTRY_GRAMS, Math.max(0.1, tenth(grams)));
    return { grams: capped, basis, check: check || capped !== tenth(grams) };
  };
  const line = parseIngredientLine(text);
  if (line.grams !== null) return done(line.grams, 'weight', false);

  const sizes = sizesOf(food.servings);
  // All the line's words, "chopped" in "1 cup chopped onion" too: they pick between the food's sizes.
  const lineWords = new Set(wordsOf(prepare(text)));
  const { quantity, unit } = line;

  const byVolume = (ml: number, rough: boolean): LineAmount => {
    // The largest volume size gives the most precise density.
    const volume = largestVolume(sizes);
    if (volume) return done((ml * volume.grams) / (volume.quantity * VOLUME_ML[volume.unit]), 'volume', rough);
    // A drink's grams are its millilitres.
    if (food.liquid) return done(ml, 'volume', rough);
    return done(ml, 'guess', true);
  };

  // An explicit metric volume is the amount, as the line's own weight would be: "1 bottle (500 ml)", "4 cups (1 L)".
  if (line.ml !== null) return byVolume(line.ml, false);
  if (quantity !== null && unit !== null) {
    if (isMass(unit)) return done(quantity * WEIGHT_G[unit], 'weight', false);
    const same = bestSize(
      sizes.filter((s) => s.unit === unit),
      lineWords,
    );
    if (same) return done((quantity * same.grams) / same.quantity, 'size', false);
    if (isVolume(unit)) return byVolume(quantity * VOLUME_ML[unit], false);
    // A stick of butter is half a cup.
    if (unit === 'stick' && /butter|margarine/i.test(food.name ?? '')) return byVolume(quantity * 120, false);
  }
  if (quantity !== null && (unit === null || isSize(unit))) {
    // "1 egg", "10 cherry tomatoes": a size that counts the food itself, the one the line describes if there is one.
    const count = bestSize(
      sizes.filter((s) => s.unit === null || isSize(s.unit) || s.unit === 'piece'),
      lineWords,
    );
    if (count) {
      // A kind the food has no size for ("10 grape tomatoes" of a tomato sized "1 medium"): rough.
      const known = new Set([...wordsOf(food.name ?? ''), ...count.words]);
      const unexplained = food.name !== undefined && wordsOf(line.name).some((w) => !known.has(w));
      return done((quantity * count.grams) / count.quantity, 'size', unexplained);
    }
  }
  if (quantity === null && line.toTaste) return byVolume(VOLUME_ML.pinch, true);
  return done((quantity ?? 1) * (food.servings[0]?.grams ?? 100), 'guess', true);
}
